
import React, { Fragment, useState } from 'react';
import {
  Box,
  Grid,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material';
import {
  Apple,
  Check,
  Circle,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import CommonButton from '../../common/components/CommonButton';
import CommonFormField from '../../common/components/CommonFormField';
import LoadingSpinner from '../../common/components/LoadingSpinner';
import { generateRandomString, validateEmail } from '../../common/const';

const SignUpForm = ({ onSubmit, loading, formData, setFormData }) => {
  const theme = useTheme();
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateField = (name, value, allValues = formData) => {
    switch (name) {
      case 'firstname':
        if (!value) return 'First Name is required';
        return '';
      case 'lastname':
        if (!value) return 'Last Name is required';
        return '';
      case 'phone':
        if (!value) return 'Phone Number is required';
        if (value.includes('+91') ? value.length < 12 : value.length < 11) return 'Please enter valid phone number';
        return '';
      case 'birthdate':
        if (!value) return 'Date of Birth is required';
        return '';
      case 'address':
        if (!value) return 'Address is required';
        return '';
      case 'zipcode':
        if (!value) return 'Zipcode is required';
        return '';
      case 'state':
        if (!value) return 'State is required';
        if (!/^[A-Za-z ]+$/.test(value)) return 'State must contain only alphabetic characters';
        return '';
      case 'city':
        if (!value) return 'City is required';
        if (!/^[A-Za-z ]+$/.test(value)) return 'City must contain only alphabetic characters';
        return '';
      case 'email':
        if (!value) return 'Email is required';
        if (!validateEmail(value)) return 'Please enter a valid email address';
        return '';
      case 'password':
        if (!value) return 'Password is required';
        if (value.length < 8) return 'Password must be at least 8 characters';
        if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter';
        if (!/[a-z]/.test(value)) return 'Password must contain at least one lowercase letter';
        if (!/\d/.test(value)) return 'Password must contain at least one number';
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) return 'Password must contain at least one special character';
        return '';
      case 'confirmpassword':
        if (!value) return 'Confirm password is required';
        if (value !== allValues.password) return 'Passwords do not match';
        return '';
      case 'acceptTerms':
        if (!value) return 'You must accept the terms & conditions and privacy policy';
        return '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      const error = validateField(name, value);
      if (!error) {
        setErrors(prev => ({ ...prev, [name]: undefined }));
      } else {
        setErrors(prev => ({ ...prev, [name]: error }));
      }
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSubmit(formData);
  };

  const handleGoogleSignUp = () => {
    // Construct Google OAuth URL
    const googleAuthUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');

    // Define parameters
    const params = {
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: `${process.env.REACT_APP_BASE_URL}/google/callback`,
      response_type: 'code',
      scope: [
        'openid',
        'email',
        'profile'
      ].join(' '),
      access_type: 'offline',
      prompt: 'consent',
      state: 'signup',
      nonce: generateRandomString()
    };

    // Add parameters to URL
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        googleAuthUrl.searchParams.append(key, value);
      }
    });

    // Redirect to Google OAuth
    window.location.href = googleAuthUrl.toString();
  };

  const handleAppleSignUp = () => {
    window.location.href = `${process.env.REACT_APP_APPLE_AUTH_URL}${new URLSearchParams({
      response_type: 'code id_token',
      response_mode: 'form_post', // or 'query' for GET request
      client_id: process.env.REACT_APP_APPLE_CLIENT_ID, // Your Service ID
      redirect_uri: `${process.env.REACT_APP_BASE_URL}/apple/callback`,
      scope: 'name email',
      state: 'signup',
      nonce: generateRandomString(16),
    })}`;
  };


  const getMaxDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 11);
    return date.toISOString().split('T')[0];
  };
 

  return (
    <Fragment>
      {loading && (
        <LoadingSpinner />
      )}
      <Box sx={{ mb: 1, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: '24px' }}>
          Sign-Up & Student Enrollment
        </Typography>
        <Box sx={{ m: '5px 10px' }}>
          <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 400, color: 'text.main' }}>
            Join Dream College Path by signing up and filling out our student enrollment form.
          </Typography>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={1} p={0}>
          <Grid item xs={12} md={6}>
            <CommonFormField
              label="First Name *"
              type="email"
              name="firstname"
              value={formData.firstname}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Enter first name"
              error={errors.firstname}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CommonFormField
              label="Last Name *"
              type="email"
              name="lastname"
              value={formData.lastname}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Enter last name"
              error={errors.lastname}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CommonFormField
              label="Email *"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Enter email id"
              error={errors.email}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CommonFormField
              label="Phone Number *"
              name="phone"
              value={formData.phone}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  phone: e?.phone,
                }));
                if (errors['phone']) {
                  const error = validateField('phone', e?.phone);
                  if (!error) {
                    setErrors(prev => ({ ...prev, phone: undefined }));
                  } else {
                    setErrors(prev => ({ ...prev, phone: error }));
                  }
                }
              }}
              placeholder="Enter phone number"
              error={errors.phone}
              isPhone={true}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <CommonFormField
              label="Date of Birth *"
              type="date"
              name="birthdate"
              value={formData.birthdate}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="MM-DD-YYYY"
              error={errors.birthdate}
              maxDate={getMaxDate()}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CommonFormField
              label="Address *"
              type="email"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Enter your address"
              error={errors.address}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CommonFormField
              label="Zipcode *"
              type="number"
              name="zipcode"
              value={formData.zipcode}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Enter your zipcode"
              error={errors.zipcode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CommonFormField
              label="City *"
              type="email"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Enter your city"
              error={errors.city}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CommonFormField
              label="State *"
              type="email"
              name="state"
              value={formData.state}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Enter your state"
              error={errors.state}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CommonFormField
              label="Password *"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="•••••••••"
              error={errors.password}
              showPassword={showPassword}
              onTogglePassword={() => setShowPassword(prev => !prev)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CommonFormField
              label="Confirm Password *"
              type="password"
              name="confirmpassword"
              value={formData.confirmpassword}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="•••••••••"
              error={errors.confirmpassword}
              showPassword={showConfirmPassword}
              onTogglePassword={() => setShowConfirmPassword(prev => !prev)}
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            mt: 1,
            mb: 1,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleInputChange}
                color="secondary"
                size="small"
                icon={<Circle sx={{ fontSize: 20, color: theme.palette.background.light }} />}
                checkedIcon={
                  <Box sx={{ background: theme.palette.secondary.main, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0.3 }}>
                    <Check sx={{ color: 'white', fontSize: 12 }} />
                  </Box>
                }
                sx={{
                  padding: '4px',
                  '& .MuiSvgIcon-root': {
                    borderRadius: '50%',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '14px', color: theme.palette.background.dark }}>
                By contiuning, you agree to the
                {' '}
                <Link
                  href={`${process.env.REACT_APP_URL}my-dcp-terms-conditions`}
                  color="secondary.main"
                  sx={{ textDecoration: 'none', fontWeight: '600' }}
                  target='_blank'
                >
                  Terms of Service
                </Link>
                {' '}
                and
                {' '}
                <Link
                  href={`${process.env.REACT_APP_URL}my-dcp-privacy-policy`}
                  color="secondary.main"
                  sx={{ textDecoration: 'none', fontWeight: '600' }}
                  target='_blank'
                >
                  Privacy Policy.
                </Link>
              </Typography>
            }
            sx={{ m: 0 }}
          />
          {errors.acceptTerms && (
            <Typography variant="caption" color="error" sx={{ fontSize: '12px', mt: 0.5, display: 'block' }}>
              {errors.acceptTerms}
            </Typography>
          )}
        </Box>
        <CommonButton
          label="Sign Up"
          size="large"
          type="submit"
          disabled={loading}
          marginBottom="20px"
        />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleGoogleSignUp}
              startIcon={
                <Box
                  component="img"
                  src="https://www.google.com/favicon.ico"
                  alt="Google"
                  sx={{ width: 20, height: 20 }}
                />
              }
              sx={{
                height: '40px',
                color: 'black',
                border: '1px solid lightgray',
                '&:hover': {
                  borderColor: 'lightgray',
                  backgroundColor: 'white',
                },
              }}
            >
              Google
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleAppleSignUp}
              startIcon={
                <Apple />
              }
              sx={{
                height: '40px',
                borderColor: 'black',
                color: 'white',
                backgroundColor: 'black',
                '&:hover': {
                  borderColor: 'black',
                  backgroundColor: 'black',
                },
              }}
            >
              Apple
            </Button>
          </Grid>
        </Grid>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{' '}
            <Link href="/login" variant="body2" color="secondary.main" sx={{ fontWeight: 300, textDecoration: 'none' }}>
              Sign In
            </Link>
          </Typography>
        </Box>
      </form>
    </Fragment>
  );
};

export default SignUpForm;